import jsPDF from "jspdf";

import { drawHeader } from "../../../../component/WGRHeader";
import { drawFooter } from "../../../../component/WGRFooter";

import { drawShopTable } from "./dwdPDFTable";

import {
  handlePdf,
  PDF_ACTION,
} from "../../../../../../utils/pdfHelper";

import {
  drawDemandTotal,
  drawGrandTotal,
} from "./dwddrawGrandTotalpdf";

import { PDFCOLORSHEME } from "../../../../../../component/constent";


// =====================================================
// FORMAT
// =====================================================

function format(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}


// =====================================================
// GET DEMAND NUMBER
// =====================================================

function getDemandNumber(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return Number.MAX_SAFE_INTEGER;
  }

  const text = String(value).trim();

  /*
   * Examples:
   *
   * dn5
   * dn6
   * dn7
   * Demand No. 5
   * Demand No. 6
   * 5
   */

  const match = text.match(/\d+/);

  if (match) {
    return Number(match[0]);
  }

  return Number.MAX_SAFE_INTEGER;
}


// =====================================================
// NORMALIZE DEMAND NAME
// =====================================================

function getDemandName(row) {
  const value =
    row["Demand No."] ??
    row.Demand ??
    row.DemandNo ??
    row.DemandNumber ??
    row.DispReportType ??
    "Unknown Demand";

  return String(value).trim() || "Unknown Demand";
}


// =====================================================
// GROUP BY DEMAND
// =====================================================

function groupByDemand(rows = []) {
  const groups = {};

  rows.forEach((row) => {
    const demand = getDemandName(row);

    if (!groups[demand]) {
  groups[demand] = {
    rows: [],

    allocations: {},

    total: {
      dl: 0,
      ds: 0,
      ocl: 0,
      ocs: 0,
      total: 0,
    },
  };
}

    groups[demand].rows.push(row);

    // -----------------------------------------------
    // Allocation
    // -----------------------------------------------

    const allocation =
      String(
        row.Allocation ??
        "Unknown"
      ).trim() || "Unknown";

    if (
      !groups[demand].allocations[
        allocation
      ]
    ) {
      groups[demand].allocations[
        allocation
      ] = {
        rows: [],

        total: {
          dl: 0,
          ds: 0,
          ocl: 0,
          ocs: 0,
          total: 0,
        },
      };
    }

    const allocationGroup =
      groups[demand].allocations[
        allocation
      ];

    allocationGroup.rows.push(row);

    // -----------------------------------------------
    // Calculate allocation total
    // -----------------------------------------------

    const dl = Number(
      row["DIRECT LABOUR"] || 0
    );

    const ds = Number(
      row["DIRECT STORE"] || 0
    );

    const ocl = Number(
      row["ON COST LABOUR"] || 0
    );

    const ocs = Number(
      row["ON COST STORE"] || 0
    );

    const total =
      dl +
      ds +
      ocl +
      ocs;


      // -----------------------------------------------
// Calculate demand total
// -----------------------------------------------

groups[demand].total.dl += dl;
groups[demand].total.ds += ds;
groups[demand].total.ocl += ocl;
groups[demand].total.ocs += ocs;
groups[demand].total.total += total;


    allocationGroup.total.dl += dl;
    allocationGroup.total.ds += ds;
    allocationGroup.total.ocl += ocl;
    allocationGroup.total.ocs += ocs;
    allocationGroup.total.total += total;

    
  });

  return groups;
}


// =====================================================
// DRAW DEMAND HEADER
// =====================================================

function drawDemandHeader(
  doc,
  demand
) {
  const startY = 30;

  doc.setFillColor(PDFCOLORSHEME.BACK_COLOR_GROUP1
  );

  doc.rect(
    14,
    startY - 8,
    270,
    10,
    "F"
  );

  doc.setTextColor(
    PDFCOLORSHEME.FONT_COLOR_GROUP
  );

  doc.setFont(
    "helvetica",
    "bold"
  );

  doc.setFontSize(
    PDFCOLORSHEME.FONT_SIZE_GROUP + 1
  );

  doc.text(
    `DEMAND: ${String(demand)}`,
    20,
    startY - 1
  );

  doc.setTextColor(0);
}


// =====================================================
// SORT ALLOCATIONS
// =====================================================

function sortAllocations(allocations) {
  return Object.keys(allocations).sort(
    (a, b) =>
      a.localeCompare(
        b,
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      )
  );
}


// =====================================================
// GENERATE PDF
// =====================================================

export default function generateWGRPDF(
  reportType,
  title,
  grandTotal,
  rows,
  summary,
  month,
  year
) {
  const doc = new jsPDF({
    orientation: "l",
    unit: "mm",
    format: "a4",
  });

  // =====================================================
  // GROUP DATA
  // =====================================================

  const groupedDemands =
    groupByDemand(rows);

  // =====================================================
  // SORT DEMANDS
  // =====================================================

  const demands = Object.keys(
    groupedDemands
  ).sort(
    (a, b) =>
      getDemandNumber(a) -
      getDemandNumber(b)
  );

  // =====================================================
  // MAIN HEADER
  // =====================================================

  drawHeader(
    doc,
    month,
    year,
    title
  );

  // =====================================================
  // DEMAND GROUPS
  // =====================================================

  demands.forEach(
    (demand, demandIndex) => {
      const demandData =
        groupedDemands[demand];

      // -----------------------------------------------
      // NEW PAGE WHEN DEMAND CHANGES
      // -----------------------------------------------

      if (demandIndex > 0) {
        doc.addPage();
      }

      // -----------------------------------------------
      // DEMAND HEADER
      // -----------------------------------------------

      drawDemandHeader(
        doc,
        demand
      );

      // -----------------------------------------------
      // ALLOCATIONS
      // -----------------------------------------------

      const allocations =
        sortAllocations(
          demandData.allocations
        );

      allocations.forEach(
  (allocation) => {
    const allocationData =
      demandData.allocations[
        allocation
      ];

    drawShopTable(
      doc,
      allocation,
      allocationData.rows,
      allocationData.total
    );
  }
);

// -----------------------------------------------
// DEMAND TOTAL
// -----------------------------------------------

drawDemandTotal(
  doc,
  demand,
  demandData.total
);
    }
  );

  // =====================================================
  // GRAND TOTAL
  // =====================================================

  drawGrandTotal(
    doc,
    grandTotal
  );

  // =====================================================
  // FOOTER
  // =====================================================

  drawFooter(
    doc,
    month,
    year,
    title
  );

  // =====================================================
  // VIEW PDF
  // =====================================================

  handlePdf(
    doc,
    PDF_ACTION.VIEW
  );
}